import { StateBadge } from '@/components/opportunity/state-badge';
import { edgeColorClass, formatEdge } from '@/lib/format-edge';
import { formatOdds } from '@/lib/format-odds';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { cn } from '@/lib/utils';
import type { OpportunityObservationRow } from '@tennis-scanner/shared';

// Vertical timeline of every observation that touched an opportunity
// (state transitions + odds updates). Read-only; the actions endpoints
// (snooze / pass / execute) write rows here automatically with
// reason='operator_<action>'.

interface Props {
  observations: OpportunityObservationRow[];
}

export function ObservationTimeline({ observations }: Props) {
  if (observations.length === 0) {
    return <p className="text-sm text-muted-foreground">Sin observaciones registradas todavía.</p>;
  }

  return (
    <ol className="relative space-y-4 border-l border-border pl-6">
      {observations.map((obs, idx) => (
        <li key={obs.id} className="relative">
          <span className="absolute -left-[27px] top-1 size-3 rounded-full border-2 border-card bg-info" />
          <div className="flex flex-wrap items-baseline gap-2">
            <StateBadge state={obs.stateAfter} />
            {obs.stateBefore !== obs.stateAfter && (
              <span className="text-xs text-muted-foreground">desde {obs.stateBefore}</span>
            )}
            <span className="font-mono text-xs text-muted-foreground">
              #{observations.length - idx}
            </span>
            <span className="ml-auto font-mono text-xs text-muted-foreground">
              {formatRelativeTime(obs.observedAt)}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-3 text-sm">
            <span className="text-muted-foreground">{obs.reason}</span>
            {obs.book && (
              <span className="font-mono text-foreground">
                {obs.book} @ {formatOdds(obs.odds)}
              </span>
            )}
            {obs.edgePct !== null && (
              <span className={cn('font-mono', edgeColorClass(obs.edgePct))}>
                {formatEdge(obs.edgePct)}
              </span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
